import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';

export type SearchFileParam = {
  id: string;
  searchId: string;
  field: string;
  value: string;
  date: string;
  type: string;
};

interface FiltersState {
  currentPage: 'search' | 'dashboard';
  __searchFilterByIds: Record<
    string,
    {
      filterCategory: string;
      filterByValues: Array<{ label: string; value: string | Date }>;
    }
  >;
  __dashboardFilterByIds: Record<
    string,
    {
      filterCategory: string;
      filterByValues: Array<{ label: string; value: string | Date }>;
    }
  >;

  filterByIds: Record<
    string,
    {
      filterCategory: string;
      filterByValues: Array<{ label: string; value: string | Date }>;
    }
  >;
  getFilterByIds: (currentPage: 'search' | 'dashboard') => Record<
    string,
    {
      filterCategory: string;
      filterByValues: Array<{ label: string; value: string | Date }>;
    }
  >;
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };
  setFilterByIds: (
    filterByIds: Record<
      string,
      {
        filterCategory: string;
        filterByValues: Array<{ label: string; value: string | Date }>;
      }
    >,
    currentPage: 'search' | 'dashboard',
  ) => void;
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => void;
  clearFilterByIds: (
    field: string,
    currentPage: 'search' | 'dashboard',
  ) => void;
  clearFilterByIdsByValue: (
    field: string,
    value: string | Date,
    currentPage: 'search' | 'dashboard',
  ) => void;
  clearFilterByIdsByValues: (
    field: string,
    values: string[],
    currentPage: 'search' | 'dashboard',
  ) => void;
}

const storeCreator: StateCreator<FiltersState> = (set, get) => ({
  currentPage: 'dashboard',
  __searchFilterByIds: {},
  __dashboardFilterByIds: {},
  filterByIds: {},
  pagination: {
    pageIndex: 0,
    pageSize: 11,
  },
  getFilterByIds: (currentPage: 'search' | 'dashboard') =>
    currentPage === 'search'
      ? get().__searchFilterByIds
      : get().__dashboardFilterByIds,
  setFilterByIds: (
    filterByIds: Record<
      string,
      {
        filterCategory: string;
        filterByValues: Array<{ label: string; value: string | Date }>;
      }
    >,
    currentPage: 'search' | 'dashboard',
  ) =>
    set({
      ...(currentPage === 'search'
        ? { __searchFilterByIds: filterByIds }
        : { __dashboardFilterByIds: filterByIds }),
    }),
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => {
    set({ pagination });
  },
  clearFilterByIds: (field: string, currentPage: 'search' | 'dashboard') => {
    set((state) => ({
      ...(currentPage === 'search'
        ? {
            __searchFilterByIds: Object.fromEntries(
              Object.entries(state.__searchFilterByIds || {}).filter(
                ([key]) => key !== field,
              ),
            ),
          }
        : {
            __dashboardFilterByIds: Object.fromEntries(
              Object.entries(state.__dashboardFilterByIds || {}).filter(
                ([key]) => key !== field,
              ),
            ),
          }),
    }));
  },
  clearFilterByIdsByValue: (
    field: string,
    fieldValue: string | Date,
    currentPage: 'search' | 'dashboard',
  ) => {
    const { getFilterByIds } = get();
    const filteringField = getFilterByIds(currentPage)[field];
    if (filteringField) {
      const newFilterByIds = { ...getFilterByIds(currentPage) };
      newFilterByIds[field] = {
        filterCategory: filteringField.filterCategory,
        filterByValues: filteringField.filterByValues.filter(
          (filter) => filter.value !== fieldValue,
        ),
      };
      set({
        ...(currentPage === 'search'
          ? { __searchFilterByIds: newFilterByIds }
          : { __dashboardFilterByIds: newFilterByIds }),
      });
    }
  },
  clearFilterByIdsByValues: (
    field: string,
    values: string[],
    currentPage: 'search' | 'dashboard',
  ) => {
    const { getFilterByIds } = get();
    const filteringField = getFilterByIds(currentPage)[field];
    if (filteringField) {
      const newFilterByIds = { ...getFilterByIds(currentPage) };
      newFilterByIds[field] = {
        filterCategory: filteringField.filterCategory,
        filterByValues: filteringField.filterByValues.filter(
          (filter) => !values.includes(filter.value as string),
        ),
      };
      set({
        ...(currentPage === 'search'
          ? { __searchFilterByIds: newFilterByIds }
          : { __dashboardFilterByIds: newFilterByIds }),
      });
    }
  },
});

export const useFiltersStore = create<FiltersState>()(
  persist(storeCreator, {
    name: 'filters',
    partialize: (state) => ({
      // filterByIds: state.filterByIds,

      __searchFilterByIds: state.__searchFilterByIds,
      __dashboardFilterByIds: state.__dashboardFilterByIds,
    }),
  }),
);
